import { KnownSearchResultItemDtoType, SearchResultItemDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Paths, createPath } from '../../../../app/router/app.router';
import { SearchResultItemDtoSubType } from './search.constants';
import { dataString } from './search.utils';

/**
 * Builds the in-app detail route for a search result.
 *
 * Detail routes are project-scoped, so every non-project type needs an owning project id:
 * we use `data.projectID` when present, otherwise fall back to the currently-selected project.
 * Returns `undefined` when no project is known — the row is then non-clickable.
 */
export function getResultPath(item: SearchResultItemDto, fallbackProjectID?: string): string | undefined {
  switch (item.type) {
    case KnownSearchResultItemDtoType.Project:
      return createPath(Paths.project, { projectID: item.id });

    case KnownSearchResultItemDtoType.UserOrAccount: {
      // Couriers can span projects, so only click through on their own projectID — never the fallback.
      if (item.subType === SearchResultItemDtoSubType.Courier) {
        const courierProjectID = dataString(item.data, 'projectID');
        return courierProjectID
          ? createPath(Paths.courier, { projectID: courierProjectID, courierID: item.id })
          : undefined;
      }
      const projectID = dataString(item.data, 'projectID') ?? fallbackProjectID;
      if (!projectID) {
        return undefined;
      }
      // Admin accounts live on the team page (its :teamMemberID param is an account id);
      // occupants/customers/users go to the customer detail page.
      if (item.subType === SearchResultItemDtoSubType.Account) {
        return createPath(Paths.teamMember, { projectID, teamMemberID: item.id });
      }
      return createPath(Paths.customer, { projectID, userID: item.id });
    }

    case KnownSearchResultItemDtoType.Hub: {
      // There is no hub detail page — the project's hubs list is the closest destination.
      const projectID = dataString(item.data, 'projectID') ?? fallbackProjectID;
      return projectID ? createPath(Paths.hubs, { projectID }) : undefined;
    }

    case KnownSearchResultItemDtoType.Delivery: {
      const projectID = dataString(item.data, 'projectID') ?? fallbackProjectID;
      return projectID ? createPath(Paths.delivery, { projectID, deliveryID: item.id }) : undefined;
    }

    case KnownSearchResultItemDtoType.Order: {
      const projectID = dataString(item.data, 'projectID') ?? fallbackProjectID;
      return projectID ? createPath(Paths.order, { projectID, orderID: item.id }) : undefined;
    }

    default:
      return undefined;
  }
}
